import { Component } from '@theme/component';

const COMPLEMENT_MAP = {
  'Business Shirt': ['Pants', 'Tuxedo', 'Accessories'],
  Pants: ['Business Shirt', 'Accessories'],
  'Sports Jacket': ['Business Shirt', 'Pants', 'Accessories'],
  Accessories: ['Business Shirt', 'Pants', 'Business Suit', 'Tuxedo'],
  'Business Suit': ['Business Shirt', 'Accessories'],
  Tuxedo: ['Business Shirt', 'Accessories'],
};

const OCCASION_TAGS = ['work', 'party', 'wedding', 'leisure'];

const collectionProductsCache = new Map();

class CompleteYourLook extends Component {
  #loaded = false;
  #abortController = null;

  connectedCallback() {
    super.connectedCallback();
    const observer = new IntersectionObserver(
      (entries, obs) => {
        if (!entries[0]?.isIntersecting) return;
        obs.disconnect();
        if (!this.#loaded) {
          this.#loaded = true;
          this.#load();
        }
      },
      { rootMargin: '0px 0px 500px 0px' }
    );
    observer.observe(this);
  }

  disconnectedCallback() {
    if (this.#abortController) {
      this.#abortController.abort();
    }
  }

  async #load() {
    this.#abortController = new AbortController();
    const signal = this.#abortController.signal;

    try {
      const cartRes = await fetch('/cart.js', { signal });
      const cart = await cartRes.json();

      if (!cart.items?.length) {
        this.style.display = 'none';
        return;
      }

      const cartHandles = cart.items.map((i) => i.handle).filter(Boolean);

      const cartCollections = new Set();
      await Promise.all(
        Object.keys(COMPLEMENT_MAP).map(async (collHandle) => {
          try {
            if (collectionProductsCache.has(collHandle)) {
              const products = collectionProductsCache.get(collHandle);
              if (products.some((p) => cartHandles.includes(p.handle))) {
                cartCollections.add(collHandle);
              }
              return;
            }
            const handleStr = collHandle.toLowerCase().replace(/\s+/g, '-');
            const r = await fetch(`/collections/${handleStr}/products.json?limit=50`, { signal });
            if (!r.ok) return;
            const { products } = await r.json();
            collectionProductsCache.set(collHandle, products);
            if (products.some((p) => cartHandles.includes(p.handle))) {
              cartCollections.add(collHandle);
            }
          } catch (_) {}
        })
      );

      const compCollections = new Set();
      for (const c of cartCollections) {
        (COMPLEMENT_MAP[c] || []).forEach((x) => compCollections.add(x));
      }

      const cartOccasionTags = new Set();
      for (const item of cart.items) {
        const rawTags = item.product_tags || '';
        const tags = typeof rawTags === 'string' ? rawTags.split(',') : [];
        for (const tag of tags) {
          const t = tag.trim().toLowerCase();
          if (OCCASION_TAGS.includes(t)) cartOccasionTags.add(t);
        }
      }

      const seenHandles = new Set(cartHandles);
      const candidates = [];
      const maxProducts = parseInt(this.dataset.maxProducts, 10) || 5;

      for (const collHandle of compCollections) {
        if (candidates.length >= maxProducts) break;
        try {
          let products = collectionProductsCache.get(collHandle);
          if (!products) {
            const handleStr = collHandle.toLowerCase().replace(/\s+/g, '-');
            const r = await fetch(`/collections/${handleStr}/products.json?limit=50`, { signal });
            if (!r.ok) continue;
            const data = await r.json();
            products = data.products;
            collectionProductsCache.set(collHandle, products);
          }
          for (const p of products) {
            if (seenHandles.has(p.handle)) continue;
            if (cartOccasionTags.size > 0) {
              const pTags = (p.tags || []).map((t) => t.toLowerCase());
              if (![...cartOccasionTags].some((t) => pTags.includes(t))) continue;
            }
            candidates.push(p);
            seenHandles.add(p.handle);
            if (candidates.length >= maxProducts) break;
          }
        } catch (_) {}
      }

      if (!candidates.length) {
        if (cartOccasionTags.size > 0) {
          const q = [...cartOccasionTags].map((t) => `tag:${t}`).join(' OR ');
          await this.#renderSection(q, cartHandles);
        } else {
          this.style.display = 'none';
        }
        return;
      }

      const q = candidates.map((p) => `title:"${p.title}"`).join(' OR ');
      await this.#renderSection(q, cartHandles, signal);
    } catch (err) {
      if (err.name !== 'AbortError') {
        this.style.display = 'none';
      }
    }
  }

  async #renderSection(searchQuery, cartHandles, signal) {
    const sectionId = this.dataset.sectionId;
    const url =
      `/search?q=${encodeURIComponent(searchQuery)}` +
      `&type=product` +
      `&section_id=${encodeURIComponent(sectionId)}` +
      `&cart_handles=${encodeURIComponent(cartHandles.join(','))}`;

    try {
      const res = await fetch(url, { signal });
      if (!res.ok) { this.style.display = 'none'; return; }

      const html = await res.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const newEl = doc.querySelector('complete-your-look');

      if (!newEl || !newEl.querySelector('[data-has-recommendations="true"]')) {
        this.style.display = 'none';
        return;
      }

      this.innerHTML = newEl.innerHTML;
    } catch (err) {
      if (err.name !== 'AbortError') {
        this.style.display = 'none';
      }
    }
  }
}

if (!customElements.get('complete-your-look')) {
  customElements.define('complete-your-look', CompleteYourLook);
}